

import React, { createContext, ReactElement, useEffect, useState } from 'react';
import {
    ProjectClientT,
    ObjectContainerClientT,
    ViewClientT,
    MappingClientT,
    FileCollectionT,
    TourClientT,
    VideoFileCollectionItemT,
    FileItemInCollectionT
} from 'bovaljaren-models';
import pym from '../js/pym.v1.min.js';

import { useLocalStore, useObserver } from 'mobx-react-lite';
import { PanZoom } from 'panzoom';
import { isLandscape, isMobile } from '../utils/app.helper';
import { getPolygonMeasurements } from '../components/image/mapping/mapping.helper';
import { ObjectDataHandler } from '../utils/object.helper';
import { AppContextT, AppStateT, PresentationState, FullscreenT, ExitFullsceenT, FavoriteState, FilterState, LoadingT } from './app.state.types';
import { useQuery } from './app.init';
import { useParams } from 'react-router-dom';


/* eslint-disable */
declare global {
    interface Window {
      gtag?: any;
      pym: any;
    }
  }
  /* eslint-enable */


const handleSharing = async (title?: string, shareLink?: string) => {
    if (!title || !shareLink) return;

    try {
        await window.navigator
            .share({ title, url: shareLink })
            .then(() =>
                console.log('Hooray! Your content was shared to tha world')
            );
    }
    catch (error) {
        console.log(`Oops! I couldn't share to the world because: ${error}`);
    }
};


export const AppContext = createContext<AppContextT>({
    filterState: undefined,
    state: undefined,
    presentationState: undefined,
    loading:'loading',
    favoriteState: undefined,
    handleSharing });

type AppStoreProviderProps = {
    showContentOnboarding?: boolean;
}

let previousHeight = 0;


export const AppStoreProvider: React.FC<AppStoreProviderProps> = ({ children, showContentOnboarding = true }): ReactElement => {

    const { pId } = useParams<{ pId: string }>();
    const projectId = (pId && parseInt(pId)) || undefined;
    const [loading, setLoading] = useState<LoadingT>('loading');
    const query = useQuery();
    const state = useLocalStore<AppStateT>(() => ({
        project: undefined,
        shareLink: { value: '' },
        previewObjectId: undefined,
        selectedObjectId: undefined,
        currentViewId: undefined,
        selectedPoiId: undefined,
        fileCollections: undefined,
        pymContextFrame: {
            active: false,
            pymClient: undefined,
            setActive: (value: boolean) => {

                if (value && state?.pymContextFrame) {
                    const pymC = new pym.Child({ id: query.get('childId') });
                    state.pymContextFrame.pymClient = pymC;
                }

                if (state && state.pymContextFrame)
                    state.pymContextFrame.active = value;
            },
            sendHeight: (height: number): void => {

                if (!state.pymContextFrame?.pymClient || height === previousHeight) {
                    return;
                }
                previousHeight = height;

                state?.pymContextFrame.pymClient.sendHeight(height);
            },
            sendMessage: (messageType: string, messageContent: any) => {
                if (!state?.pymContextFrame?.pymClient || !messageType || !messageContent) {
                    return;
                }
                state?.pymContextFrame.pymClient.sendMessage(messageType, messageContent);
            }
        },
        get sitemapUrl(): string {
            return state.project?.sitemapImage?.url?.medium || '';
        },
        get objects(): ObjectContainerClientT[] | undefined {
            return state.project?.objects;
        },
        get currentView(): ViewClientT | undefined {
            return state.project?.views.find(v => v.id === state.currentViewId);
        },
        get isStartView(): boolean {
            return state.project?.primaryViewId === state.currentView?.id;
        },
        get currentViewMappingObjects(): MappingClientT[] | undefined {
            return state.currentView?.mappings.objects.filter(o => filterState.filterObjects(state.objects, state.project?.objectTypes)?.some(fO => fO.id === o.mappingEntityId));
        },
        setShareLink(value: {shareModalText?: string; value: string} | undefined): void {
            state.shareLink = value;
        },
        setPreviewObjectId(value: number | undefined): void {
            state.previewObjectId = value;
        },
        isCurrentPreviewObject(value: number | null): boolean {
            if (state.previewObjectId === undefined || value === null) return false;
            else return value === state.previewObjectId;
        },
        setProject(project: ProjectClientT): void {

            let uid: string | null = null;
            try {
                uid = (localStorage.getItem('uId'));
            }
            catch (e) {
                //
            }

            if (state.project)
                window.gtag('event', 'select_item', {
                    item_list_id: state.project.id,
                    item_list_name: state.project.name,
                    items: [
                        {
                            item_id: state.project.id,
                            item_name: state.project.name,
                            item_list_id: state.project.id,
                            item_list_name: state.project.name,
                            item_category2: `uid:${uid}`,
                            item_category3: 'project-load',
                            item_brand: state.project.branding,
                            quantity: 1
                        }
                    ]
                });
            state.project = project;

            const paramObjectId = new URLSearchParams(document.location.search).get('objectId');

            let currentViewId = project.primaryViewId;

            if (paramObjectId && project.objects.find(o => o.id === parseInt(paramObjectId))) {

                const currentObjectView = state.project.views.find(v => v.mappings.objects.find(o => o.mappingEntityId === parseInt(paramObjectId)));

                if (currentObjectView) {
                    currentViewId = currentObjectView.id;

                    if (isMobile()) {
                        const mapping = currentObjectView?.mappings.objects.find(mO => mO.mappingEntityId === parseInt(paramObjectId));
                        const measure = mapping && getPolygonMeasurements(mapping);

                        presentationState.setShowSidebarContent(false);
                        measure && presentationState.zoomInOnPosition(measure.xMid, measure.yMid);
                    }
                    state.setSelectedObjectId(parseInt(paramObjectId));
                    presentationState?.setShowOnboarding(false);
                    presentationState?.setShowObjectView(true);
                }
            }

            state.currentViewId = currentViewId;
        },
        changeView (viewId: number | null): void {
            presentationState?.resetPanzoomTranslate();
            isMobile() && presentationState?.showObjectView && presentationState.setShowObjectView(false);
            isMobile() && presentationState && !presentationState.showSidebarContent && presentationState.setShowSidebarContent(true);
            isMobile() && presentationState?.showPoiInfo && presentationState.setShowPoiInfo(false);
            state?.setCurrentViewId(viewId);
        },
        resetViewMappingPreview() {
        },
        setSelectedObjectId(objectId: number | null): void {
            const selectedObj = state.project?.objects.find(o => o.id === objectId);

            let uid: string | null = null;
            try {
                uid = (localStorage.getItem('uId'));
            }
            catch (e) {
                //
            }

            if (state.project && objectId && selectedObj)
                window.gtag('event', 'select_item', {
                    item_list_id: state.project.id,
                    item_list_name: state.project.name,
                    items: [
                        {
                            item_id: selectedObj.id,
                            item_name: selectedObj.name,
                            item_list_id: state.project.id,
                            item_list_name: state.project.name,
                            item_category: state.project.objectTypes.find(obT => obT.id === selectedObj.objectTypeId)?.name,
                            item_category2: `uid:${uid}`,
                            item_category3: 'object-click',
                            item_brand: state.project.branding,
                            item_variant: state.project.statuses.find(oS => oS.id === selectedObj.statusId)?.singular,
                            quantity: 1
                        }
                    ]
                });
            state.selectedObjectId = objectId;
        },
        setSelectedPoiId(poiId: number | null): void {
            state.selectedPoiId = poiId;
        },
        setCurrentViewId(viewId: number | null): void {
            const currView = viewId && state.project?.views.find(v => v.id === viewId);


            let uid: string | null = null;
            try {
                uid = (localStorage.getItem('uId'));
            }
            catch (e) {
                //
            }

            if (state.project && currView)
                window.gtag('event', 'select_item', {
                    item_list_id: state.project.id,
                    item_list_name: state.project.name,
                    items: [
                        {
                            item_id: currView.id,
                            item_name: currView.name,
                            item_list_id: state.project.id,
                            item_list_name: state.project.name,
                            item_category3: 'view-click',
                            item_category2: `uid:${uid}`,
                            quantity: 1
                        }
                    ]
                });
            state.currentViewId = viewId;
        },
        setFileCollections(fileCollections: FileCollectionT[]): void {
            state.fileCollections = fileCollections;
        }
    }));


    const getClientHeight = () => {
        const height = document.getElementsByClassName('bv-aspect-outer')[0]?.clientHeight;

        return height;
    };

    window.addEventListener('resize', () => {
        presentationState.setWidth();
    });

    const presentationState = useLocalStore<PresentationState>(() => ({
        showObjectView: false,
        showPoiInfo: false,
        showDrawer: false,
        showVideo: undefined,
        showShareModal: false,
        showStatusContainer:  false,
        expandNavBreadcrumbs: !isMobile(),
        modalWidth:getClientHeight(),
        fullscreen: false,
        showOnboarding: true,
        showSitemap: showContentOnboarding || !isMobile(),
        showButtons: false,
        showObjectList: false,
        showSunStudyModal: undefined,
        showImageGalleryModal: undefined,
        showSidebarContent: showContentOnboarding || !isMobile(),
        showFavoriteModal: false,
        showKeyboardInfo: true,
        activePreview: false,
        currentIframe: undefined,
        zoomScale: 1,
        panzoom: undefined,
        setFullscreen(value: boolean): void {
            presentationState.fullscreen = value;
        },
        setWidth(): void {
            presentationState.modalWidth = getClientHeight();
        },
        setShowOnboarding(value: boolean): void {
            let uid: string | null = null;
            try {
                uid = (localStorage.getItem('uId'));
            }
            catch (e) {
                //
            }

            if (state.project && !value)
                window.gtag('event', 'select_item', {
                    item_list_id: state.project.id,
                    item_list_name: state.project.name,
                    items: [
                        {
                            item_name: 'Onboarding',
                            quantity: 1,
                            item_list_id: state.project.id,
                            item_list_name: state.project.name,
                            item_category3: 'onboarding-click',
                            item_category2: `uid:${uid}`
                        }
                    ]
                });

            presentationState.showOnboarding = value;
            presentationState.showButtons = !value;
            presentationState.setPanzoomPause(value);
        },
        setShowPoiInfo(value: boolean): void {
            presentationState.showPoiInfo = value;
        },
        setShowDrawer(value: boolean): void {
            presentationState.showDrawer = value;
            presentationState.setPanzoomPause(value);
        },
        setShowButton(value: boolean): void {
            presentationState.showButtons = value;
        },
        setShowShareModal(value: boolean): void {
            presentationState.showShareModal = value;
        },
        setExpandNavBreadcrumbs(value: boolean): void {
            presentationState.expandNavBreadcrumbs = value;
        },
        setActivePreview(value: boolean): void {
            presentationState.activePreview = value;
        },
        setShowVideo(value: FileItemInCollectionT | undefined): void {
            presentationState.showVideo = value;
        },
        setShowSunStudyModal(value: FileCollectionT | undefined): void {
            presentationState.showSunStudyModal = value;
        },
        setShowImageGalleryModal(value: FileCollectionT | undefined): void {
            presentationState.showImageGalleryModal = value;
        },
        setShowObjectView(value: boolean): void {
            presentationState.showObjectView = value;

            if (!value) {
                state.resetViewMappingPreview();
                state.selectedObjectId = undefined;
            }

            !isMobile() && presentationState.setPanzoomPause(value);
        },
        setShowSidebarContent(value: boolean): void {
            presentationState.showSidebarContent = value;
        },
        setCurrentIframe(value: TourClientT | undefined) {
            presentationState.currentIframe = value;
        },
        setShowSitemap(value: boolean): void {
            presentationState.showSitemap = value;
        },
        setShowKeyboardInfo(value: boolean): void {
            presentationState.showKeyboardInfo = value;
        },
        setShowStatusContainer(value: boolean): void {
            presentationState.showStatusContainer = value;
        },
        setShowObjectList(value: boolean): void {
            presentationState.showObjectList = value;
        },
        setShowFavoriteModal(value: boolean): void {
            presentationState.showFavoriteModal = value;
            presentationState.showButtons = !value;
            presentationState.setPanzoomPause(value);
        },
        setPanzoom(panzoom: PanZoom): void {
            presentationState.panzoom = panzoom;
        },
        setZoomScale(value: number): void {
            presentationState.zoomScale = value;
            //presentationState.showKeyboardInfo = false;
        },
        setPanzoomPause(pause: boolean): void {

            if (pause) {
                presentationState.panzoom?.pause();
            }
            else {
                presentationState.panzoom?.resume();
            }
        },
        resetPanzoomTranslate(): void {
            const main = document.getElementById('bv-main');

            if (main) {
                main.classList.remove('bv-main--shrink');
            }
            presentationState.panzoom?.moveTo(0, 0);
            presentationState.panzoom?.zoomAbs(0, 0, 1);
        },
        zoomInOnPosition(x: number, y: number): void {
            const main = document.getElementById('bv-main');
            const m = main && { x: main.clientWidth, y: main.clientHeight };

            if (m && main) {
                main.classList.add('bv-main--shrink');

                if (isLandscape()) {
                    presentationState.panzoom?.moveTo(0, (y / 100) * m.y * -3);
                }
                else {
                    presentationState.panzoom?.zoomTo(Math.round(m.x * (x / 100)), Math.round(m.y * (y / 100) * 1.5), isLandscape() ? 1 : 2);
                }
            }
        },
        maximize(): void {

            const enterFullscreen = (element: FullscreenT): void => {

                if (element.requestFullscreen) {
                    element.requestFullscreen();
                }
                else if (element.mozRequestFullScreen) {
                    element.mozRequestFullScreen();
                }
                else if (element.webkitRequestFullscreen) {
                    element.webkitRequestFullscreen();
                }
                else if (element.webkitEnterFullscreen) {
                    element.webkitEnterFullscreen();
                }
                else if (element.msRequestFullscreen) {
                    element.msRequestFullscreen();
                }
                else if (window.location.hostname !== state?.project?.domain) {
                    state?.project && state?.project.domain !== '' && window.open(`https://${state?.project?.domain}bv/v3/${state?.project?.id}`, '_blank');
                }
                presentationState.fullscreen = true;
            };

            const exitFullscreen = (element: ExitFullsceenT): void => {

                if (element.exitFullscreen) {
                    element.exitFullscreen();
                }
                else if (element.mozCancelFullScreen) {
                    element.mozCancelFullScreen();
                }
                else if (element.webkitExitFullscreen) {
                    element.webkitExitFullscreen();
                }
                else if (element.msExitFullscreen) {
                    element.msExitFullscreen();
                }

                presentationState.fullscreen = false;
            };


            const frameElement = (window.frameElement) as unknown as FullscreenT & ExitFullsceenT;
            const root = document.getElementById('bv-root-element') as unknown as FullscreenT;
            const doc = document as unknown as ExitFullsceenT;


            (presentationState.fullscreen) ? exitFullscreen(frameElement || doc) : enterFullscreen(frameElement || root);
        }
    }));

    const favoriteState = useLocalStore<FavoriteState>(() => {
        let storedIds;
        try {
            storedIds = localStorage.getItem(`favorites-${projectId}`)?.split(',');
        }
        catch (e) {
            //
        }

        return ({
            objectIds: query.get('favIt')?.split(',')
                .map(id => parseInt(id)) || storedIds?.map(id => parseInt(id)) || [],

            addFavorite(objectId: number): void {
                !this.isFavored(objectId) && this.objectIds.push(objectId);
                localStorage.setItem(`favorites-${projectId}`, this.objectIds.filter(d => !isNaN(d)).join(','));
            },
            removeFavorite(objectId: number): void {
                this.objectIds.splice(this.objectIds.indexOf(objectId), 1);
                localStorage.setItem(`favorites-${projectId}`, this.objectIds.filter(d => !isNaN(d)).join(','));
            },
            isFavored(objectId: number): boolean {
                return this.objectIds.some(id => id === objectId);
            }
        });
    });

    const filterState = useLocalStore<FilterState>(() => ({
        filters: undefined,
        appliedFilters: [],
        filterObjects(objects, objectTypes): ObjectContainerClientT[] | undefined {
            if (filterState.appliedFilters.length !== 0 && objects) {
                return objects.filter(o => {
                    const objectType = objectTypes?.find(ot => ot.id === o.objectTypeId);

                    if (!objectType) {
                        return null;
                    }
                    const numberOfRooms = ObjectDataHandler.getNumberOfRooms(o, objectType);

                    return numberOfRooms !== null && filterState.appliedFilters.find(aF => aF === numberOfRooms);
                });
            }

            return objects;
        },
        applyRoomFilter(numberOfRooms, checked, objects): void {
            checked ?
                filterState.appliedFilters.push(numberOfRooms) :
                filterState.appliedFilters = filterState.appliedFilters.filter(fA => fA !== numberOfRooms);
        },
        configureFilters(objects, objectTypes): void {

            if (objects && objectTypes) {
                const filter = ObjectDataHandler.getDistinctSortedRoomQuantities(objects, objectTypes);
                filterState.filters = filter;
            }
        }
    }));

    return useObserver(() => (<>
        <AppContext.Provider value={{
            filterState,
            state,
            handleSharing,
            setLoading,
            favoriteState,
            presentationState,
            loading
        }} children={children}/>
    </>
    ));
};